// The WGSL twin of the sonar sweep's own program (`../sweep.ts`, card 59,
// R548).
//
// The picture is the GLSL pair's, line for line: one quad of `2R x 2R` over a
// running reveal, one `atan2`, one `fract` and a four-stop ramp. What the
// port changes is what the language forces.
//
//  * One module holds both stages, and the one varying is a struct both read.
//  * Every uniform is a member of the generated block, so each read is
//    `U.uName`. The sweep carries a block of its OWN — nine uniforms, no
//    shared world contract — because the pass draws outside the world passes
//    and reads no data plane at all.
//  * `atan(y, x)` is `atan2(y, x)`. The one-argument `atan` keeps its name in
//    WGSL, so the two-argument form is the one that is renamed.
//  * `discard` stays. It marks the invocation and the shader runs on, so the
//    fragment still returns a value and WGSL is satisfied.
//
// The pass reads no texture, so the twin calls no sampler at all. `glMod` is
// not needed here either: the ramp runs on `fract`, which both languages give
// the same answer for.

import type { AttribSpec } from '../../gfx/types';
import type { UniformSpec } from '../prelude';
import { cornerDecodeWgsl } from '../../gl/corner';
import { wgslModule } from './module';

/** The one varying the two stages share: the world point under the pixel. */
const SWEEP_VARYINGS_WGSL = `struct VsOut {
  @builtin(position) pos: vec4f,
  @location(0) vWorld: vec2f,
}
`;

/**
 * The quad, in WORLD px.
 *
 * `aCorner` is the per-vertex corner index of `gl/corner.ts` (R553), and the
 * shift count is `1u` because WGSL takes the count as a `u32`.
 */
const SWEEP_VERTEX_WGSL = `@vertex
fn vs(in: VsIn) -> VsOut {
  var out: VsOut;
${cornerDecodeWgsl()}
  out.vWorld = U.uCentre + (corner * 2.0 - 1.0) * U.uR;
  out.pos = vec4f((U.uViewProj * vec3f(out.vWorld, 1.0)).xy, 0.0, 1.0);
  return out;
}
`;

/**
 * The afterglow and the radius.
 *
 * The output is PREMULTIPLIED, because the one blend state of the backend is
 * `src: one, dst: one-minus-src-alpha`. `uLine` is the turning radius: its
 * alpha and its width in world px, drawn as a distance to the ray, so the
 * whole sweep stays one quad.
 */
const SWEEP_FRAGMENT_WGSL = `const TAU: f32 = 6.28318530718;

@fragment
fn fs(in: VsOut) -> @location(0) vec4f {
  let d = in.vWorld - U.uCentre;
  let rr = length(d);
  if (rr > U.uR) { discard; }
  let t = fract((atan2(d.y, d.x) - U.uAngle) / TAU);
  // The four-stop ramp, as three clamped mixes.
  var a = mix(U.uStopAlpha.x, U.uStopAlpha.y,
    clamp((t - U.uStopAt.x) / max(1e-5, U.uStopAt.y - U.uStopAt.x), 0.0, 1.0));
  a = mix(a, U.uStopAlpha.z,
    clamp((t - U.uStopAt.y) / max(1e-5, U.uStopAt.z - U.uStopAt.y), 0.0, 1.0));
  a = mix(a, U.uStopAlpha.w,
    clamp((t - U.uStopAt.z) / max(1e-5, U.uStopAt.w - U.uStopAt.z), 0.0, 1.0));
  // The turning radius: the distance from the point to the ray.
  let ray = vec2f(cos(U.uAngle), sin(U.uAngle));
  let along = dot(d, ray);
  let off = abs(d.x * ray.y - d.y * ray.x);
  if (along > 0.0) {
    a = max(a, U.uLine.x * (1.0 - smoothstep(U.uLine.y * 0.5, U.uLine.y, off)));
  }
  a = a * U.uFade;
  return vec4f(U.uRgb * a, a);
}
`;

/**
 * The whole WGSL module of the sweep: the header and both stages, assembled
 * by the one rule of `./module.ts` like every other batch.
 *
 * `specs` is the block in order, which is what `ProgramSource` carries, so
 * the struct can never name a uniform the packer does not write.
 */
export function sweepModuleWgsl(
  specs: readonly UniformSpec[], attribs: readonly AttribSpec[],
): string {
  return wgslModule({
    uniforms: specs, attribs, varyings: SWEEP_VARYINGS_WGSL,
    vertex: SWEEP_VERTEX_WGSL, body: SWEEP_FRAGMENT_WGSL,
  });
}
